<template>
  <div class="hazard-rectification-detail">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <div class="hazard-title">{{ getPageTitle() }}</div>
        <div class="hazard-info">
          <span class="hazard-code">编号: {{ detailData.hazardCode }}</span>
          <el-tag
            :type="getStatusType(detailData.acceptanceResult)"
            class="status-tag"
          >
            {{ getStatusText(detailData.acceptanceResult) }}
          </el-tag>
        </div>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-area">
      <!-- 基础信息卡片 -->
      <div class="overview-card">
        <div class="card-header">
          <span class="card-title">基础信息</span>
        </div>
        <div class="card-content">
          <div class="info-grid">
            <div class="info-item">
              <span class="label">隐患编号</span>
              <span class="value">{{ detailData.hazardCode || "-" }}</span>
            </div>
            <div class="info-item">
              <span class="label">问题类型</span>
              <span class="value">{{ detailData.problemTypeName || "-" }}</span>
            </div>
            <div class="info-item">
              <span class="label">责任部门</span>
              <span class="value">{{
                detailData.responsibleDeptName || "-"
              }}</span>
            </div>
            <div class="info-item">
              <span class="label">整改责任人</span>
              <span class="value">{{
                detailData.responsiblePersonName || "-"
              }}</span>
            </div>
            <div class="info-item">
              <span class="label">发现日期</span>
              <span class="value">{{ detailData.discoveryDate || "-" }}</span>
            </div>
            <div class="info-item">
              <span class="label">创建时间</span>
              <span class="value">{{ detailData.createTime || "-" }}</span>
            </div>
            <div class="info-item">
              <span class="label">创建人</span>
              <span class="value">{{ detailData.createName || "-" }}</span>
            </div>
            <div
              class="info-item full-width"
              v-if="detailData.updateName || detailData.updateTime"
            >
              <span class="label">更新信息</span>
              <span class="value">
                {{
                  detailData.updateName
                    ? `${detailData.updateName} 于 ${detailData.updateTime} 更新`
                    : "-"
                }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 问题描述卡片 -->
      <div class="content-card">
        <div class="card-header">
          <span class="card-title">问题描述</span>
        </div>
        <div class="card-content" style="display: flex">
          <div>
            <div class="desc-content">
              {{ detailData.problemDescription || "无问题描述" }}
            </div>
            <div
              class="img-list"
              v-if="
                detailData.problemPhotosList &&
                detailData.problemPhotosList.length
              "
            >
              <div
                class="img-item"
                v-for="(img, index) in detailData.problemPhotosList"
                :key="index"
                @click="previewImage(img)"
              >
                <img :src="img" alt="问题图片" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 整改历史卡片 -->
      <div
        class="content-card"
        v-if="detailData.list && detailData.list.length"
      >
        <div class="card-header">
          <span class="card-title">整改备注</span>
        </div>
        <div
          class="card-content"
          style="display: flex; flex-wrap: wrap"
          v-for="(item, index) in detailData.list"
          :key="index"
        >
          <div style="width: 100%;display: flex;">
            <div style="width: 80px;">整改备注{{ index + 1 }}：</div>
            <span>
              {{ item.remark || "无整改备注" }}
            </span>
          </div>
          <div style="padding-left: 80px;">
            <div
              class="img-list"
              v-if="item.problemPhotosList && item.problemPhotosList.length"
            >
              <div
                class="img-item"
                v-for="(img, index) in item.problemPhotosList"
                :key="index"
                @click="previewImage(img)"
              >
                <img :src="img" alt="问题图片" />
              </div>
            </div>
          </div>
          <div
            style="
              width: 100%;
              margin: 20px 0;
              display: flex;
              padding-left: 80px;
              justify-content: space-between;
            "
          >
            <span>处理人：{{ item.updateName }}</span>
            <span style="margin-left: 20px">
              处理时间：{{ item.updateTime }}
            </span>
          </div>
        </div>
      </div>

      <!-- 验收结果卡片 -->
      <div class="content-card" v-if="detailData.acceptanceResult">
        <div class="card-header">
          <span class="card-title">验收结果</span>
        </div>
        <div class="card-content">
          <div class="result-grid">
            <div class="result-item">
              <span class="label">验收结果</span>
              <span class="value">{{
                getAcceptanceResultText(detailData.acceptanceResult)
              }}</span>
            </div>
            <div class="result-item" v-if="detailData.acceptancePersonName || detailData.acceptPersonName">
              <span class="label">验收人</span>
              <span class="value">{{ detailData.acceptancePersonName || detailData.acceptPersonName }}</span>
            </div>
            <div class="result-item" v-if="detailData.acceptanceDate || detailData.acceptanceTime">
              <span class="label">验收时间</span>
              <span class="value">{{ detailData.acceptanceDate || detailData.acceptanceTime }}</span>
            </div>
            <div class="result-item full-width" v-if="detailData.acceptanceSiteInfo">
              <span class="label">现场信息</span>
              <span class="value">{{ detailData.acceptanceSiteInfo }}</span>
            </div>
          </div>
          <div
            class="img-list"
            v-if="
              detailData.acceptancePhotosList &&
              detailData.acceptancePhotosList.length
            "
          >
            <div
              class="img-item"
              v-for="(img, index) in detailData.acceptancePhotosList"
              :key="index"
              @click="previewImage(img)"
            >
              <img :src="img" alt="验收照片" />
            </div>
          </div>
        </div>
      </div>

      <!-- 时间线卡片 -->
      <div class="content-card">
        <div class="card-header">
          <span class="card-title">处理进度</span>
        </div>
        <div class="card-content">
          <div class="timeline">
            <el-timeline>
              <el-timeline-item
                v-for="(activity, index) in timelineData"
                :key="index"
                :timestamp="activity.timestamp"
                :type="activity.type"
                :color="activity.color"
                :size="activity.size"
                :icon="activity.icon"
              >
                {{ activity.content }}
              </el-timeline-item>
            </el-timeline>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getSafetyHazardRectifyDetail, getDictionary } from "../api";

export default {
  name: "HazardRectificationDetail",
  props: {
    transmitData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      detailData: {},
      // 状态映射
      statusMap: {
        0: "待验收",
        1: "验收通过",
        2: "验收不通过",
        3: "不适用",
      },
      statusTypeMap: {
        0: "primary",
        1: "success",
        2: "warning",
        3: "info",
        // 3: "success",
        // 4: "warning",
        // 5: "primary",
        // 6: "success",
        // 7: "warning",
      },
      acceptanceMap: {
        1: "验收通过",
        2: "验收不通过",
      },
    };
  },
  computed: {
    // 时间线数据
    timelineData() {
      const timeline = [];

      // 创建时间
      if (this.detailData.createTime) {
        timeline.push({
          content: "隐患记录创建",
          timestamp: this.detailData.createTime,
          type: "primary",
          color: "#0bbd87",
          icon: "el-icon-document-add",
        });
      }

      // 整改状态变化
      if (this.detailData.rectifyStatus !== undefined) {
        timeline.push({
          content: `状态变更为: ${this.getStatusText(
            this.detailData.rectifyStatus
          )}`,
          timestamp: this.detailData.updateTime || this.detailData.createTime,
          type: "success",
          color: "#e6a23c",
          icon: "el-icon-refresh",
        });
      }

      // 验收结果
      if (this.detailData.acceptanceResult) {
        timeline.push({
          content: `验收结果: ${this.getAcceptanceResultText(
            this.detailData.acceptanceResult
          )}`,
          timestamp: this.detailData.acceptanceTime,
          type: "warning",
          color: "#67c23a",
          icon: "el-icon-check",
        });
      }

      return timeline;
    },
  },
  watch: {
    transmitData: {
      immediate: true,
      handler(newVal) {
        if (newVal && (newVal.id || newVal.row)) {
          this.loadDetailData();
        }
      },
    },
  },
  methods: {
    formatDateByPattern(date, format = "yyyy-MM-dd HH:mm:ss") {
      if (!date) return "-";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "-";

      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const day = d.getDate();
      const hours = d.getHours();
      const minutes = d.getMinutes();
      const seconds = d.getSeconds();

      return format
        .replace("yyyy", year)
        .replace("MM", String(month).padStart(2, "0"))
        .replace("dd", String(day).padStart(2, "0"))
        .replace("HH", String(hours).padStart(2, "0"))
        .replace("mm", String(minutes).padStart(2, "0"))
        .replace("ss", String(seconds).padStart(2, "0"));
    },
    getImgSrc(srcStr, type = "src") {
      let srcAll = [];
      if (!srcStr) return type == "src" ? "" : [];
      srcAll = srcStr.split(",");
      if (type == "src") {
        return srcAll[0];
      } else {
        return srcAll;
      }
    },
    // 获取页面标题
    getPageTitle() {
      if (this.transmitData.type === "view") {
        return "查看隐患整改";
      } else if (this.transmitData.type === "edit") {
        return "编辑隐患整改";
      } else {
        return "隐患整改详情";
      }
    },

    // 加载详情数据
    async loadDetailData() {
      // 如果有ID，优先调用API获取详情
      if (this.transmitData.id) {
        try {
          const { data } = await getSafetyHazardRectifyDetail(
            this.transmitData.id
          );
          data.data.list.forEach((val) => {
            val.problemPhotosList = this.getImgSrc(val.remarkPhotos, "list");
            val.updateTime = this.formatDateByPattern(val.updateTime);
          });
          this.detailData = data.data || {};
          this.detailData.problemPhotosList = this.getImgSrc(
            this.detailData.problemPhotos,
            "list"
          );
          this.detailData.acceptancePhotosList = this.getImgSrc(
            this.detailData.acceptancePhotos,
            "list"
          );
        } catch (error) {
          console.error("加载详情失败:", error);
          // 如果API调用失败，使用传入的行数据作为备选
          if (this.transmitData.row) {
            this.detailData = this.transmitData.row;
            this.detailData.problemPhotosList = this.getImgSrc(
              this.detailData.problemPhotos,
              "list"
            );
            this.detailData.acceptancePhotosList = this.getImgSrc(
              this.detailData.acceptancePhotos,
              "list"
            );
          }
        }
      } else if (this.transmitData.row) {
        // 直接使用传入的行数据
        this.detailData = this.transmitData.row;
        this.detailData.problemPhotosList = this.getImgSrc(
          this.detailData.problemPhotos,
          "list"
        );
        this.detailData.acceptancePhotosList = this.getImgSrc(
          this.detailData.acceptancePhotos,
          "list"
        );
      }
    },

    // 获取状态类型
    getStatusType(status) {
      return this.statusTypeMap[status] || "info";
    },

    // 获取状态文本
    getStatusText(status) {
      return this.statusMap[status] || "未知状态";
    },

    // 获取验收结果文本
    getAcceptanceResultText(result) {
      return this.acceptanceMap[result] || "未知结果";
    },

    // 预览图片
    previewImage(url) {
      this.$alert(`<img src="${url}" style="max-width: 100%;">`, "图片预览", {
        dangerouslyUseHTMLString: true,
        showConfirmButton: false,
        customClass: "image-preview-modal",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.hazard-rectification-detail {
  padding: 20px;
  background-color: #fff;
  height: 100%;
  overflow-y: auto;

  .page-header {
    background: white;
    padding: 20px 24px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;

    .header-left {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .hazard-title {
        font-size: 24px;
        font-weight: 700;
        color: #303133;
        line-height: 1.2;
      }

      .hazard-info {
        display: flex;
        align-items: center;
        gap: 12px;

        .hazard-code {
          font-size: 14px;
          color: #909399;
        }

        .status-tag {
          font-size: 12px;
          padding: 4px 8px;
          border-radius: 4px;
        }
      }
    }
  }

  .content-area {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .overview-card,
  .content-card {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    .card-header {
      background: #f8f9fa;
      padding: 16px 24px;
      border-bottom: 1px solid #ebeef5;

      .card-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
      }
    }

    .card-content {
      padding: 24px;
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    .info-item {
      display: flex;
      flex-direction: column;
      gap: 4px;

      &.full-width {
        grid-column: 1 / -1;
      }

      .label {
        font-size: 14px;
        color: #909399;
        font-weight: 500;
      }

      .value {
        font-size: 14px;
        color: #303133;
        font-weight: 500;
      }
    }
  }

  .desc-content {
    margin-bottom: 10px;
    line-height: 1.5;
    white-space: pre-line;
    color: #333;
  }

  .img-list {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 10px;
  }

  .img-item {
    width: 80px;
    height: 80px;
    border: 1px solid #ddd;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s;
  }

  .img-item:hover {
    border-color: #409eff;
    transform: scale(1.05);
  }

  .img-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .result-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .result-item {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .label {
        font-size: 14px;
        color: #909399;
        font-weight: 500;
      }

      .value {
        font-size: 14px;
        color: #303133;
        font-weight: 500;
      }
    }
  }

  .timeline {
    padding: 10px 0;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .hazard-rectification-detail {
    padding: 12px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
/* 全局样式调整 */
.image-preview-modal {
  width: auto;
  max-width: 90%;
}

.image-preview-modal .el-message-box__content {
  padding: 0;
}

.image-preview-modal .el-message-box__content img {
  display: block;
  margin: 0 auto;
}
</style>
